import type { PropType } from "vue";

import { omit } from "radash";
import { computed, ref } from "vue";

import type { DefineProps } from "@/lib/composables/componentComposable";

import * as useButton from "@/lib/components/logic/atoms/useButton";
import {
  emitsDefinition,
  propsDefinition,
} from "@/lib/composables/componentComposable";

const props = propsDefinition({
  placeholder: { type: String, default: "Search" },
  ariaLabel: { type: String, default: "Search" },
  alwaysOpen: { type: Boolean, default: false },
  fluid: { type: Boolean, default: false },
  ...omit(useButton.styleProps, [
    "prefixIcon",
    "suffixIcon",
    "fluid",
    "textAlign",
    "checked",
    "rounded",
    "opaque",
  ]),
  actionType: {
    type: String as PropType<
      "quaternary" | "secondary" | "tertiary" | "tertiary-soft"
    >,
    default: "tertiary-soft",
  },
  size: {
    type: String as PropType<"lg" | "md" | "sm">,
    default: "sm",
  },
});

type UseButtonSearchEmit = (
  event: "blur" | "focus" | "search",
  value: string,
) => void;

const emits = emitsDefinition(["search", "focus", "blur"]);

type UseButtonSearchProps = DefineProps<typeof props>;

function use(props: UseButtonSearchProps, emit: UseButtonSearchEmit) {
  const term = ref("");

  const isDisabled = computed(() => props.disabled);

  function submit() {
    emit("search", term.value);
  }

  function handleFocus() {
    emit("focus", term.value);
  }

  function handleBlur() {
    emit("blur", term.value);
  }

  return {
    term,
    disabled: isDisabled,
    submit,
    handleFocus,
    handleBlur,
  };
}

export type { UseButtonSearchEmit, UseButtonSearchProps };
export { emits, props, use };
